import React, { useRef, useEffect } from 'react';
import { Circle } from 'lucide-react';
import { ImageOptimizer } from '@/components/common/ImageOptimizer';
import { ResumeComparison } from '@/components/ui/ResumeComparison';
import { CriteriaSelector } from '@/components/ui/CriteriaSelector';
import { CandidateNotification } from '@/components/ui/CandidateNotification';
import { CandidateMagnetAnimation } from '@/components/ui/CandidateMagnetAnimation';
import FulcrumAnimation from '@/components/FulcrumAnimation';
const TripleAdvantage = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Use IntersectionObserver only for non-critical below-the-fold content
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('opacity-100');
        observer.unobserve(entry.target);
      }
    }, {
      threshold: 0.1,
      rootMargin: '200px'
    } // Increased rootMargin for earlier loading
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);
  return <section ref={sectionRef} className="py-16 bg-brand-offwhite opacity-0 transition-opacity duration-1000" style={{
    contain: 'content'
  }} // Improve paint performance
  >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-brand-darkgrey mb-4">
            How Cutshort gets you to a shortlist faster
          </h1>
          <h2 className="text-xl md:text-2xl font-semibold text-brand-darkgrey mb-6">
            Your leverage to hire in today's noisy world
          </h2>
          <p className="text-lg text-brand-grey max-w-3xl mx-auto leading-relaxed">
            Cutshort provides the leverage across talent discovery and recruiting workflows — so recruiters can focus on judgment, not grunt work.
          </p>
        </div>

        {/* Fulcrum Animation - Section 3 */}
        {/* <div className="mb-12">
          <FulcrumAnimation />
        </div> */}

        <h2 className="text-xl md:text-2xl font-semibold text-brand-darkgrey text-center mb-8">
          Your shortlist is powered by
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* First advantage */}
          <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow will-change-transform">
            <div className="flex items-center mb-4">
              <div className="bg-brand-blue/10 w-12 h-12 rounded-full flex items-center justify-center mr-3 relative">
                <Circle className="h-6 w-6 text-brand-blue" />
                <span className="absolute text-xs font-bold text-brand-blue">1</span>
              </div>
              <h3 className="text-xl font-semibold text-brand-darkgrey">
                Intuitive understanding of what you want
              </h3>
            </div>
            <div className="w-full mb-4 h-[250px]">
              <CriteriaSelector autoplay={true} autoplayDelay={2000} />
            </div>
          </div>

          {/* Second advantage */}
          <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow will-change-transform">
            <div className="flex items-center mb-4">
              <div className="bg-brand-blue/10 w-12 h-12 rounded-full flex items-center justify-center mr-3 relative">
                <Circle className="h-6 w-6 text-brand-blue" />
                <span className="absolute text-xs font-bold text-brand-blue">2</span>
              </div>
              <h3 className="text-xl font-semibold text-brand-darkgrey">
                Smart mapping of relevant talent
              </h3>
            </div>
            <div className="w-full mb-4">
              <ResumeComparison />
            </div>
          </div>

          {/* Third advantage */}
          <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow will-change-transform">
            <div className="flex items-center mb-4">
              <div className="bg-brand-blue/10 w-12 h-12 rounded-full flex items-center justify-center mr-3 relative">
                <Circle className="h-6 w-6 text-brand-blue" />
                <span className="absolute text-xs font-bold text-brand-blue">3</span>
              </div>
              <h3 className="text-xl font-semibold text-brand-darkgrey">
                Backed by India's largest premium tech talent pool
              </h3>
            </div>
            <div className="w-full mb-4">
              <CandidateMagnetAnimation />
            </div>
          </div>
        </div>
      </div>
    </section>;
};
export default TripleAdvantage;